import TinCan from 'tincanjs';
import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import SettingActions from '../actions/SettingActionCreator';
import SettingStore from '../stores/SettingStore';
import LRSConstants from '../constants/LRSConstants';
import ActivityStore from '../stores/ActivityStore';
import * as schema from '../schema/statements';
import { normalize } from 'normalizr';

const APP_LRS_ALLOW_FAIL = false;

const triggerVerbs = ['http://adlnet.gov/expapi/verbs/passed', 'http://adlnet.gov/expapi/verbs/failed'];

export const getFullVerb = (verb) => {
    switch (verb) {
        case 'launched':
        case 'passed':
        case 'failed':
        case 'completed':
            return `http://adlnet.gov/expapi/verbs/${verb}`;

        default:
            throw new Error(`Invalid verb '${verb}'`);
    }
};

const LRSConfig = {
    LRS_APP_ID: '',
    LRS_APP_HOMEPAGE: '',
    LRS_ENDPOINT: '',
    LRS_KEY: '',
    LRS_SECRET: ''
};

const LRS = {
    connection: null,

    updateSettings: function() {
        const settings = SettingStore.getAll();
        if (settings && settings.length !== 0) {
            settings.forEach((setting) => {
                if (setting.value !== undefined && LRSConfig.hasOwnProperty(setting.name)) {
                    LRSConfig[setting.name] = setting.value;
                }
            });
            if (!!LRSConfig['LRS_APP_HOMEPAGE'] && LRSConfig['LRS_APP_HOMEPAGE'].endsWith('/')) {
                LRSConfig['LRS_APP_HOMEPAGE'] = LRSConfig['LRS_APP_HOMEPAGE'].slice(0, -1);
            }
        }
    },

    connect: function() {
        if (this.connection === null){
            try {
                let count = 0;
                while (LRSConfig.LRS_ENDPOINT === '' && count < 10000) {
                    ++count;
                }

                if (LRSConfig.LRS_ENDPOINT !== '') {
                    const connectionConfig = {
                        endpoint:  LRSConfig.LRS_ENDPOINT,
                        username:  LRSConfig.LRS_KEY,
                        password:  LRSConfig.LRS_SECRET,
                        allowFail: APP_LRS_ALLOW_FAIL
                    };
                    this.connection = new TinCan.LRS(connectionConfig);
                }
            }
            catch (ex) {
                console.error("Failed to setup LRS object: " , ex);
            }
        }
        return this.connection;
    },

    saveStatement: function(statement, cb){
        if (this.connect() !== null) {
            const callback = (err, xhr) => {
                if (err !== null) {
                    if (xhr !== null) {
                        console.error("Failed to save statement: %s (%s)", xhr.responseText, xhr.status );
                    } else {
                        console.error("Failed to save statement: ", err);
                    }
                }

                if (cb) { cb(); }
            };

            this.connection.saveStatement( statement, {callback: callback});
        }

    },

    saveLaunchCourseStatement: function(user, courseId, courseName, registrationId, callback){
        const fullName = `${user.firstName} ${user.lastName}`;
        const activityId =`${window.location.origin}/training/modules/${courseId}`;

        const statement = new TinCan.Statement({
            actor: this.getAgentAccount(user.id, fullName),
            verb: {
                id: LRSConstants.Verbs.LAUNCHED,
                display: {
                    "en-US": "launched"
                }
            },
            context: {
                registration: registrationId
            },
            object: {
                id: activityId,
                definition: {
                    name: {
                        "und": courseName
                    },
                    type: "http://adlnet.gov/expapi/activities/course"
                }
            }
        });

        this.saveStatement(statement, callback);
    },

    saveCustomStatement: function(user, verb, activity, callback) {
        const fullName = `${user.firstName} ${user.lastName}`;
        const activityObj = LRS.getActivityObject(activity);
        const statement = new TinCan.Statement({
            actor: this.getAgentAccount(user.id, fullName),
            verb:  verb,
            object: activityObj
        });
        this.saveStatement(statement, callback);
    },

    findByRegistrationId: function(registrationId){
        this.connect();
        this.connection.queryStatements(
            {
                params: {
                    registration: registrationId,
                    limit: 1000,
                },
                callback: this.storeActivities,
            }
        );

    },

    getUserActivity: function(userId, courseId) {
        const types = {
            success:    ActionTypes.LRS_USER_ACTIVITY_SUCCESS,
            failure:    ActionTypes.LRS_USER_ACTIVITY_FAILURE
        };
        const payload = {userId, courseId};

        const params = { limit: 500 };
        if (!!courseId) {
            params.activity = new TinCan.Activity({id: courseId});
            params.related_activities = true;
        }

        const agents = this.getAgentAccounts(userId);
        this.connect();
        // short term solution: fire it once for the verbs and filter for that.
        triggerVerbs.forEach((v) => {
            params.verb = new TinCan.Verb({id: v});
            agents.forEach((agent) => {
                params.agent = agent;
                this.connection.queryStatements({
                    params: params,
                    callback: this.dispatchActivities.bind(this, types, payload),
                });
            });
        });
    },

    getCourseActivity: function(courseId, userId) {
        if (!!userId) { return this.getUserActivity(userId, courseId); } // guard

        const params = {
            activity: new TinCan.Activity({id: courseId}),
            related_activities: true,
            limit: 500,
        };

        this.connect();
        triggerVerbs.forEach((verb) => {
            params.verb = new TinCan.Verb({id: verb});
            this.connection.queryStatements({
                params,
                callback: this.storeActivities,
            });
        });
    },

    getRegistrationActivity: function(registrationId) {
        const types = {
            success:    ActionTypes.LRS_REGISTRATION_ACTIVITY_SUCCESS,
            failure:    ActionTypes.LRS_REGISTRATION_ACTIVITY_FAILURE
        };
        const params = {
            registration: registrationId
        };
        this.connect().queryStatements({
            params: params,
            callback: this.dispatchActivities.bind(this, types, {})
        });
    },

    getStatements: function(options = {}) {
        const types = {
            request:    ActionTypes.LRS_STATEMENTS_REQUEST,
            success:    ActionTypes.LRS_STATEMENTS_SUCCESS,
            failure:    ActionTypes.LRS_STATEMENTS_FAILURE
        };

        const params = {
            related_activities: true,
            limit: options.limit || 500,
        };

        if (!!options.verb) {
            params.verb = new TinCan.Verb({id: getFullVerb(options.verb)});
        }

        const payload = {...params};

        CentricDispatcher.throttleDispatch({ type: types.request, payload });
        this.connect().queryStatements({
                params,
                callback: this.dispatchNormalizedActivities.bind(this, types, payload, schema.statements)
            })
        ;

    },

    storeActivities:  function (err, sr) {
        if (err !== null) {
            console.error("Failed to query statements: ", err);
            return;
        }

        if (!!sr.more) {
            LRS.connection.moreStatements({url: sr.more, callback: LRS.storeActivities});
        }
        sr.statements.forEach(ActivityStore.addActivity);
        ActivityStore.emitChange();
    },

    clearActivities: function(registration) {
        CentricDispatcher.dispatchPayload(ActionTypes.LRS_REGISTRATION_ACTIVITY_CLEANUP, {registration});
    },

    dispatchActivities: function(types, payload, error, result) {
        const { success, failure } = types;
        if (error !== null ) {
            CentricDispatcher.dispatch({ type: failure, error, payload: Object.assign({}, payload) });
            return;
        }

        if (!!result.more) {
            this.connection.moreStatements({url: result.more, callback: this.storeActivities.bind(this, types, Object.assign({more: true}, payload))});
        }
        CentricDispatcher.dispatch({ type: success, data: result.statements, payload: Object.assign({}, payload) });
    },

    dispatchNormalizedActivities: function(types, payload, schema, error, result) {
        if (error !== null && !!types.failure ) {
            CentricDispatcher.dispatch({ type: types.failure, error, payload: Object.assign({}, payload) });
            return;
        }

        if (!!result.more) {
            this.connection.moreStatements({url: result.more, callback: this.dispatchNormalizedActivities.bind(this, types, {...payload }, schema)});
        }
        CentricDispatcher.dispatch({ type: types.success, data: result.statements && normalize(result.statements, schema), payload: { ...payload, more: !!result.more } });
    },

    getAgentAccounts: function(id, fullName) {
        const params = {
            account: {
                name: fullName || `${LRSConfig.LRS_APP_ID}|${id}`,
                homePage: LRSConfig.LRS_APP_HOMEPAGE
            }
        };

        const ret = [];
        ret.push(new TinCan.Agent(params));
        params.account.homePage += '/';
        ret.push(new TinCan.Agent(params));

        return ret;
    },

    getAgentAccount: function(id, fullName){

        const agentName = `${LRSConfig.LRS_APP_ID}|${id}`;

        return new TinCan.Agent({
            name: fullName,
            account: {
                name: agentName,
                homePage: LRSConfig.LRS_APP_HOMEPAGE
            }

        });
    },

    getActivityObject: function(activity) {
        let activityObj = false;
        if (typeof activity === 'object') {
            activityObj = {
                id: this.getActivityId(activity)
            };
            if (!!activity.name) {
                activityObj.definition = {name: {'en-GB': activity.name }};
                if (!!activity.description) {
                    activityObj.definition.description = {'en-GB': activity.description};
                }
            }
        }
        return activityObj;
    },

    getActivityId: function(activity) {
        let activityId = false;
        if (typeof activity === 'object') {
            switch (activity.type) {
                case 'document':
                    if (!!activity.id) {
                        activityId = `${LRSConfig.LRS_APP_HOMEPAGE}/activities/document/${activity.id}`;
                    }
                    break;

                default:
            }
        }
        return activityId;
    }
};

LRS.updateSettings = LRS.updateSettings.bind(LRS);
LRS.listener = SettingStore.addListener(LRS.updateSettings);
SettingActions.getAll();

export default LRS;
